import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ScannerSection, SCAN_SUCCESS_FLASH_MS, type ScannerSectionHandle } from '../scanner';
import OrderDetailCameraBar, { CameraIcon, PICK_SCAN_BAR_VARIANTS } from '../orders/orderdetail/OrderDetailCameraBar';
import { extractStorageIdFromQr } from '../orders/orderdetail/scanningLogic';
import { QR_UNREADABLE_STORAGE_ID_MESSAGE } from '../scanner/qrScanMessages';
import { getLabelByStorageIdPrefix } from '../../services/shelfIndexAPI';
import { findSKU, getVariantWeight, updateVariantWeight, type ProductVariantWeight } from '../../services/SKUListAPI';
import {
  MESSAGE_TONES,
  cameraMessageClass,
  statusSlotMessageClass,
  useStatusMessage,
} from '../../hooks/useStatusMessage';
import ProductInfoModal from '../shared/ProductInfoModal';
import '../orders/orderdetail/pick-list.css';
import '../orders/OrdersPilot.css';
import '../labels/PrintBoxLabel.css';
import '../labels/ScanLabel.css';

export const PRODUCT_WEIGHT_SKU_QUERY = 'sku';

const SKU_SCAN_PROMPT = 'Scan a SKU or Label to update weight';
const RSV_SKU_RE = /RSV-[A-Z0-9]+(?:-[A-Z0-9]+)*/i;

function parseSkuParam(value: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function extractRsvSkuFromText(text: string): string | null {
  const compact = text.replace(/\s+/g, '').toUpperCase();
  const match = compact.match(RSV_SKU_RE);
  return match ? match[0] : null;
}

function ProductWeight(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const scannerCaptureRef = useRef<ScannerSectionHandle>(null);
  const loadedSkuRef = useRef<string | null>(null);
  const urlSku = parseSkuParam(searchParams.get(PRODUCT_WEIGHT_SKU_QUERY));
  const [webcamOn, setWebcamOn] = useState(() => !urlSku);
  const [variant, setVariant] = useState<ProductVariantWeight | null>(null);
  const [showModal, setShowModal] = useState(() => Boolean(urlSku));
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const {
    message,
    tone: messageTone,
    invalidInput: errorInvalidInput,
    flashKey: messageKey,
    fades: messageFades,
    showMessage,
    clearMessage,
  } = useStatusMessage();

  const resetToSkuScan = useCallback((): void => {
    loadedSkuRef.current = null;
    setVariant(null);
    setShowModal(false);
    setSubmitting(false);
    setWebcamOn(true);
    clearMessage();
    setSearchParams({}, { replace: true });
  }, [clearMessage, setSearchParams]);

  const applyVariant = useCallback(
    (next: ProductVariantWeight): void => {
      setVariant(next);
      setShowModal(true);
      setWebcamOn(false);
      clearMessage();
    },
    [clearMessage],
  );

  const loadVariant = useCallback(
    async (sku: string, presentAfterFlash = false): Promise<boolean> => {
      loadedSkuRef.current = sku;
      showMessage('Looking up product...');
      const next = await getVariantWeight(sku);
      if (!next) {
        setVariant(null);
        setShowModal(false);
        showMessage(`SKU ${sku} Not found in Product DB`, MESSAGE_TONES.ERROR, {
          invalidInput: sku,
        });
        return false;
      }
      if (presentAfterFlash) {
        window.setTimeout(() => applyVariant(next), SCAN_SUCCESS_FLASH_MS);
      } else {
        applyVariant(next);
      }
      return true;
    },
    [applyVariant, showMessage],
  );

  useEffect(() => {
    const sku = parseSkuParam(searchParams.get(PRODUCT_WEIGHT_SKU_QUERY));
    if (!sku) {
      loadedSkuRef.current = null;
      return;
    }
    if (loadedSkuRef.current === sku) return;
    void loadVariant(sku);
  }, [searchParams, loadVariant]);

  const openProduct = useCallback(
    async (sku: string): Promise<boolean> => {
      const found = await loadVariant(sku, true);
      if (found) {
        setSearchParams({ [PRODUCT_WEIGHT_SKU_QUERY]: sku }, { replace: true });
      }
      return found;
    },
    [loadVariant, setSearchParams],
  );

  const handleSkuCapture = useCallback(
    async (text: string | null): Promise<boolean> => {
      if (!text || !text.trim()) {
        showMessage(QR_UNREADABLE_STORAGE_ID_MESSAGE, MESSAGE_TONES.ERROR, { fade: true });
        return false;
      }

      const prefix = extractStorageIdFromQr(text);
      if (prefix) {
        showMessage('Looking up label...');
        const label = await getLabelByStorageIdPrefix(prefix);
        const sku = label?.sku?.trim();
        if (!sku) {
          showMessage(`Label not found in DB for storage ID ${prefix}`, MESSAGE_TONES.ERROR, {
            invalidInput: prefix,
          });
          return false;
        }
        return openProduct(sku);
      }

      const candidate = extractRsvSkuFromText(text) ?? text.replace(/\s+/g, '').toUpperCase();
      showMessage('Looking up product...');
      const result = await findSKU(candidate);
      if (result?.SKUFound && result.sku) {
        return openProduct(result.sku);
      }
      showMessage(`Could not match SKU. Scan input: ${candidate}`, MESSAGE_TONES.ERROR, {
        invalidInput: candidate,
      });
      return false;
    },
    [openProduct, showMessage],
  );

  const handleSaveWeight = useCallback(
    async (weightOz: number): Promise<boolean> => {
      if (!variant || submitting) return false;
      setSubmitting(true);
      const updated = await updateVariantWeight(variant.sku, weightOz);
      if (!updated) {
        setSubmitting(false);
        return false;
      }
      setShowSuccess(true);
      window.setTimeout(() => setShowSuccess(false), 1000);
      resetToSkuScan();
      return true;
    },
    [resetToSkuScan, submitting, variant],
  );

  const handleCloseModal = useCallback((): void => {
    resetToSkuScan();
  }, [resetToSkuScan]);

  const messageBody = errorInvalidInput && message.includes(errorInvalidInput) ? (
    <>
      {message.split(errorInvalidInput)[0]}
      <span className="pbl-message-invalid">{errorInvalidInput}</span>
      {message.split(errorInvalidInput).slice(1).join(errorInvalidInput)}
    </>
  ) : (
    message
  );

  const cameraMessageOverlay = message ? (
    <div key={messageKey} className={cameraMessageClass(messageTone, messageFades)} role="status">
      {messageBody}
    </div>
  ) : null;

  const cameraButton = (
    <button
      type="button"
      className="order-detail-bar-camera-btn order-detail-bar-camera-btn-center"
      onClick={() => setWebcamOn(true)}
      aria-label="Open camera"
    >
      <CameraIcon size={28} />
    </button>
  );

  return (
    <div className={`pbl-container sl-page${webcamOn ? ' sl-camera-open' : ''}`}>
      {showSuccess ? (
        <div className="success-flash">
          <div className="success-message">✓ Success!</div>
        </div>
      ) : null}

      <div className="pbl-page-header-status" role="status" aria-live="polite">
        {message && !webcamOn ? (
          <div key={messageKey} className={statusSlotMessageClass(messageTone)}>
            {messageBody}
          </div>
        ) : null}
      </div>

      {showModal && variant ? null : (
        <div className="sl-prompt">{SKU_SCAN_PROMPT}</div>
      )}

      {showModal && variant ? (
        <ProductInfoModal
          item={{ sku: variant.sku, name: variant.name }}
          onClose={handleCloseModal}
          weightUpdate={{
            onSave: handleSaveWeight,
            saving: submitting,
          }}
        />
      ) : null}

      <OrderDetailCameraBar
        open={webcamOn}
        onShutter={() => scannerCaptureRef.current?.capture()}
        barVariant={PICK_SCAN_BAR_VARIANTS.CAMERA}
        centerActions={cameraButton}
      >
        <ScannerSection
          ref={scannerCaptureRef}
          mode="hybrid"
          onCapture={handleSkuCapture}
          webcamOn={webcamOn}
          setWebcamOn={setWebcamOn}
          className="webcam-capture-container pbl-shelf-indexer-scanner"
          overlay={cameraMessageOverlay}
          cornerAction={
            <button
              type="button"
              className="webcam-viewport-close-btn"
              onClick={(event) => {
                event.stopPropagation();
                setWebcamOn(false);
              }}
              aria-label="Close camera"
            >
              ×
            </button>
          }
        />
      </OrderDetailCameraBar>
    </div>
  );
}

export default ProductWeight;
